import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['image', 'lens', 'toggleButton', 'zoomIcon', 'zoomOffIcon']

  connect() {
    this.zooming = false
    this.zoomEnabled = false 
    this.zoomLevel = 3
    this.setupImageObserver()
  }

  disconnect() {
    if (this.imageObserver) {
      this.imageObserver.disconnect()
    }
  }

  setupImageObserver() {
    this.imageObserver = new MutationObserver(() => {
      if (this.zooming) {
        this.updateZoom(this.lastEvent || { 
          clientX: this.imageTarget.getBoundingClientRect().left + this.imageTarget.offsetWidth / 2,
          clientY: this.imageTarget.getBoundingClientRect().top + this.imageTarget.offsetHeight / 2
        })
      }
    })
    
    this.imageObserver.observe(this.imageTarget, {
      attributes: true,
      attributeFilter: ['src']
    })
  }

  toggleZoomEnabled() {
    this.zoomEnabled = !this.zoomEnabled

    if (this.zoomEnabled) {
      this.zoomIconTarget.classList.remove('hidden')
      this.zoomOffIconTarget.classList.add('hidden')
      this.toggleButtonTarget.classList.add('ring-2', 'ring-accent-600')
      this.toggleButtonTarget.title = 'Desativar zoom'
    } else {
      this.zoomIconTarget.classList.add('hidden')
      this.zoomOffIconTarget.classList.remove('hidden')
      this.toggleButtonTarget.classList.remove('ring-2', 'ring-accent-600')
      this.toggleButtonTarget.title = 'Ativar zoom'

      if (this.zooming) {
        this.hideZoom()
      }
    }
  }

  mouseEnter(event) {
    if (this.isTouchDevice() || !this.zoomEnabled) return
    this.showZoom(event)
  }

  mouseMove(event) {
    if (this.isTouchDevice() || !this.zooming) return
    this.updateZoom(event)
  }

  mouseLeave() {
    if (this.isTouchDevice()) return
    this.hideZoom()
  }

  touchStart(event) {
    if (!this.zoomEnabled) return
    
    event.preventDefault()
    this.zooming = !this.zooming
    
    if (this.zooming) {
      const touch = event.touches[0]
      const mouseEvent = new MouseEvent('mousemove', {
        clientX: touch.clientX,
        clientY: touch.clientY
      })
      this.showZoom(mouseEvent)
      this.updateZoom(mouseEvent)
    } else {
      this.hideZoom()
    }
  }

  touchMove(event) {
    if (!this.zooming) return
    event.preventDefault()
    
    const touch = event.touches[0]
    const mouseEvent = new MouseEvent('mousemove', {
      clientX: touch.clientX,
      clientY: touch.clientY
    })
    this.updateZoom(mouseEvent)
  }

  showZoom(event) {
    this.zooming = true
    this.imageTarget.style.cursor = 'zoom-in'

    if (!this.hasLensTarget) {
      this.createLens()
    }
    
    this.lensTarget.classList.remove('hidden')
    this.updateZoom(event)
  }

  updateZoom(event) {
    const img = this.imageTarget
    const lens = this.lensTarget
    const rect = img.getBoundingClientRect()

    this.lastEvent = event
    
    // Calculate cursor position relative to image
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top
    
    // Lens dimensions
    const lensWidth = lens.offsetWidth
    const lensHeight = lens.offsetHeight
    
    // Position lens centered on cursor
    let lensX = x - lensWidth / 2
    let lensY = y - lensHeight / 2
    
    // Keep lens within image bounds
    lensX = Math.max(0, Math.min(lensX, rect.width - lensWidth))
    lensY = Math.max(0, Math.min(lensY, rect.height - lensHeight))
    
    lens.style.left = `${lensX}px`
    lens.style.top = `${lensY}px`
    
    // Calculate background position for zoom effect
    const bgX = (x / rect.width) * 100
    const bgY = (y / rect.height) * 100
    
    lens.style.backgroundImage = `url(${img.src})`
    lens.style.backgroundSize = `${rect.width * this.zoomLevel}px ${rect.height * this.zoomLevel}px`
    lens.style.backgroundPosition = `${bgX}% ${bgY}%`
  }

  hideZoom() {
    this.zooming = false
    this.imageTarget.style.cursor = 'default'
    
    if (this.hasLensTarget) {
      this.lensTarget.classList.add('hidden')
    }
  }

  createLens() {
    const lens = document.createElement('div')
    lens.setAttribute('data-image-zoom-target', 'lens')
    lens.className = 'absolute hidden w-48 h-48 border-2 border-white shadow-lg rounded-md pointer-events-none bg-no-repeat sm:w-72 sm:h-72'
    lens.style.boxShadow = '0 0 0 2px rgba(0,0,0,0.2), 0 4px 12px rgba(0,0,0,0.3)'
    this.element.appendChild(lens)
  }

  isTouchDevice() {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0
  }
}
